import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  contractService, 
  Contract, 
  CreateContractRequest, 
  FullUpdateContractRequest,
  UpdateContractRequest, 
  UpdateVolumeDistributionRequest
} from '@/services/contractService';
import { toast } from 'sonner';

export const useContracts = (params?: {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  businessUnitId?: number;
  includeDeleted?: boolean;
}) => {
  return useQuery({
    queryKey: ['contracts', params],
    queryFn: () => contractService.getAll(params).then(res => res.data),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useContract = (id: number) => {
  return useQuery({
    queryKey: ['contracts', id],
    queryFn: () => contractService.getById(id).then(res => res.data),
    enabled: !!id,
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: contractService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract created successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to create contract';
      toast.error(message);
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateContractRequest }) =>
      contractService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contracts', variables.id] });
      toast.success('Contract updated successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to update contract';
      toast.error(message);
    },
  });
};

export const useFullUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FullUpdateContractRequest }) =>
      contractService.fullUpdate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contracts', variables.id] });
      toast.success('Contract updated successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to update contract';
      toast.error(message);
    },
  });
};

export const useUpdateVolumeDistribution = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateVolumeDistributionRequest }) =>
      contractService.updateVolumeDistribution(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Volume distribution updated successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to update volume distribution';
      toast.error(message);
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: contractService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract deleted successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to delete contract';
      toast.error(message);
    },
  });
};

export const useRestoreContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: contractService.restore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      toast.success('Contract restored successfully');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string; title?: string } } };
      const message = err.response?.data?.detail || err.response?.data?.title || 'Failed to restore contract';
      toast.error(message);
    },
  });
};
